/**
 * Field Agent — Business Quick Onboarding
 * Phase 2G (2026-04-26)
 * /portal/field-agent/business-quick
 *
 * Field agents create business-quick users for shops they visit.
 * Backend: /api/field-agent/business-quick/* (FIELD_AGENT role required).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Loader2, Building2, Plus, RefreshCw, ArrowLeft } from "lucide-react";
import { NewBusinessDialog } from "../Admin/AdminBusinessQuick";

export default function FieldAgentBusinessQuick() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/field-agent/business-quick/users");
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load my businesses");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fa-business-quick">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/portal/field-agent/weekly-rollup" className="text-slate-400 hover:text-slate-700">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <Building2 className="w-5 h-5 text-emerald-600" />
            <h1 className="text-base font-bold">Business Quick — Onboard shops</h1>
          </div>
          <Button data-testid="fabq-new" size="sm" onClick={() => setShowNew(true)}>
            <Plus className="w-4 h-4 mr-1" />New
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-3 space-y-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 text-xs text-emerald-900">
          You can onboard shops, retailers, salons or any business that drops packages at PUDO. They get a 6-digit PIN by email and log in at <strong>liantiaoja.com/business-quick/login</strong>. <strong>No cash at PUDO.</strong>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500">My onboarded businesses ({rows.length})</div>
          <button onClick={reload} className="text-xs text-slate-500 underline inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" />Refresh
          </button>
        </div>

        {busy ? (
          <div className="text-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin inline mr-1" />Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-slate-500" data-testid="fabq-empty">
            <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No businesses yet. Tap <strong>+ New</strong> to add one while you're on the field.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map(r => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`fabq-row-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm">{r.business_name}</div>
                  {r.is_active ? (
                    <span className="text-xs bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">ACTIVE</span>
                  ) : (
                    <span className="text-xs bg-rose-100 text-rose-800 rounded-full px-2 py-0.5">SUSPENDED</span>
                  )}
                </div>
                <div className="text-xs text-slate-600">{r.contact_name} · {r.phone}</div>
                <div className="text-xs text-slate-500">{r.address}, {r.parish}</div>
                <div className="text-[11px] text-slate-400">{r.email}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showNew && (
        <NewBusinessDialog
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); reload(); }}
          endpoint="/field-agent/business-quick/users"
        />
      )}
    </div>
  );
}
